import { useEffect, useMemo, useState, useContext, useRef } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { AuthContext } from "../AuthContext";
import jsQR from "jsqr";
import { useTranslation } from "../i18n/LanguageContext";
import Avatar from "../components/Avatar";
import ChatMessageList from "../components/ChatMessageList";
import MessageComposer from "../components/MessageComposer";
import { useChatNotifications } from "../contexts/ChatNotificationsContext";
import { useChatThread } from "../hooks/useChatThread";
import { useAutoScroll } from "../hooks/useAutoScroll";
import { formatChatTimestamp } from "../utils/formatDate";
import { getRoleLabel } from "../utils/roleUtils";

// Dev-only diagnostics for the QR decode/payload pipeline — never logs in
// production builds. Helps confirm whether jsQR actually decoded a frame and
// why a decoded payload was or wasn't accepted as a PartyBuddy buddy QR.
const qrDebug = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[PartyBuddy QR]", ...args);
  }
};

// The canonical PartyBuddy Buddy QR payload is the user's raw Firebase uid
// (this is exactly what Profile.js's <QRCodeSVG value={profile.id} /> has
// always encoded — see Profile.js). We also accept an explicit
// `partybuddy://user/<uid>` URI (a more self-describing format, in case a
// future QR source or share link uses it) and a small `{ "uid": "..." }`
// JSON payload, purely for forward/backward compatibility — none of this
// changes what PartyBuddy itself generates today, so every QR code already
// printed/shown by existing users keeps working unchanged.
const extractUidFromScanPayload = (raw) => {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const uriMatch = trimmed.match(/^partybuddy:\/\/user\/([^/?#\s]+)/i);
  if (uriMatch) return decodeURIComponent(uriMatch[1]);

  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed.uid === "string" && parsed.uid.trim()) {
        return parsed.uid.trim();
      }
    } catch {
      // Not JSON — fall through to legacy handling below.
    }
  }

  // Reject QR codes that are clearly not a PartyBuddy uid (e.g. a random
  // web URL) instead of trying to look up an obviously-wrong value.
  if (/^https?:\/\//i.test(trimmed)) return null;
  // A bare uid is a compact alphanumeric token; anything containing spaces
  // or newlines (a photographed poster, a boarding pass, etc.) is not one.
  if (/\s/.test(trimmed) || trimmed.length > 128) return null;

  return trimmed;
};

function Buddies() {
  const { user } = useContext(AuthContext);
  const [buddies, setBuddies] = useState([]);
  const [selectedBuddy, setSelectedBuddy] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scanError, setScanError] = useState("");
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [buddiesError, setBuddiesError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  // QR scan result — the recognized profile plus its buddy-relationship
  // status relative to the current user, shown as a bubble/card. Adding a
  // buddy always requires an explicit tap on the bubble's action button;
  // scanning alone never sends a request.
  const [scannedProfile, setScannedProfile] = useState(null); // { id, ...profileData, relation }
  const [scannedAction, setScannedAction] = useState("idle"); // idle | sending | sent
  const { t, language } = useTranslation();
  const chatNotifications = useChatNotifications();
  const chatsByBuddy = useMemo(
    () => chatNotifications?.chatsByBuddy || {},
    [chatNotifications]
  );

  // Single realtime chat-thread hook (shared with ChatPage.js) — this is
  // what actually fixes the "no live updates while the embedded chat is
  // open" bug: one onSnapshot on the resolved chat doc, cleaned up whenever
  // the selected buddy changes or this page unmounts.
  const {
    messages,
    loading: chatLoading,
    error: chatError,
    sending,
    sendMessage,
  } = useChatThread(user?.uid, selectedBuddy?.id);
  const { containerRef, bottomRef, hasNewBelow, scrollToBottom } = useAutoScroll(
    messages,
    selectedBuddy?.id
  );

  // Buddies sorted by most-recent-message-first; buddies with no
  // conversation yet (lastMessageAtMillis === 0) naturally sort to the end
  // while remaining fully visible.
  const sortedBuddies = useMemo(() => {
    return [...buddies].sort((a, b) => {
      const aTime = chatsByBuddy[a.id]?.lastMessageAtMillis || 0;
      const bTime = chatsByBuddy[b.id]?.lastMessageAtMillis || 0;
      return bTime - aTime;
    });
  }, [buddies, chatsByBuddy]);

  const getPreviewText = (buddy) => {
    const entry = chatsByBuddy[buddy.id];
    if (!entry?.lastMessage) return t("chat.noConversationYet", "No conversation yet");
    if (entry.lastMessage.type === "event") return t("chat.eventSharedPreview", "Event shared");
    return (entry.lastMessage.text || "").slice(0, 60);
  };
  // We manage the camera stream and <video> element ourselves (rather than
  // delegating to a third-party scanner component) so we can guarantee the
  // element that gets the MediaStream is the exact one visibly rendered on
  // screen. `canvasRef` is an offscreen canvas used only to grab frames for
  // QR decoding via jsQR — it is never shown.
  const videoRef = useRef(null);
  const canvasRef = useRef(null);


  useEffect(() => {
    const fetchBuddies = async () => {
      if (!user) return;
      setBuddiesError("");
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const currentData = userSnap.data() || {};
          const buddyIds = currentData.friends || [];
          setIncomingRequests(currentData.incomingRequests || []);
          const buddyData = await Promise.all(
            buddyIds.map(async (id) => {
              const ref = doc(db, "users", id);
              const snap = await getDoc(ref);
              return snap.exists() ? { id, ...snap.data() } : null;
            })
          );
          setBuddies(buddyData.filter(Boolean));
        }
      } catch (err) {
        console.error("Failed to load buddies:", err);
        setBuddiesError(t("buddies.loadError", "Couldn't load your buddies. Please try again."));
      }
    };

    fetchBuddies();
  }, [user, reloadToken, t]);

  // Clear the "active chat" marker when leaving the Buddies page so a chat
  // left open here doesn't keep suppressing notifications elsewhere in the app.
  useEffect(() => {
    return () => chatNotifications?.setActiveChatBuddy(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openChat = (buddy) => {
    setSelectedBuddy(buddy);
    chatNotifications?.setActiveChatBuddy(buddy.id);
    if (!buddies.some((item) => item.id === buddy.id)) {
      setScanError(t('buddies.chatLocked', 'Chat is available after confirmation.'));
      return;
    }
    chatNotifications?.markChatRead(buddy.id);
  };

  const closeChat = () => {
    setSelectedBuddy(null);
    chatNotifications?.setActiveChatBuddy(null);
  };

  // Keep this buddy's unread count cleared as new messages arrive while the
  // conversation is open here (matches ChatPage.js's behavior).
  useEffect(() => {
    if (selectedBuddy && messages.length > 0) {
      chatNotifications?.markChatRead(selectedBuddy.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, selectedBuddy?.id]);

  const acceptRequest = async (requesterId) => {
    const currentRef = doc(db, "users", user.uid);
    const requesterRef = doc(db, "users", requesterId);
    const [currentSnap, requesterSnap] = await Promise.all([getDoc(currentRef), getDoc(requesterRef)]);
    const currentData = currentSnap.data() || {};
    const requesterData = requesterSnap.data() || {};

    await Promise.all([
      updateDoc(currentRef, {
        friends: [...new Set([...(currentData.friends || []), requesterId])],
        incomingRequests: (currentData.incomingRequests || []).filter((id) => id !== requesterId),
      }),
      updateDoc(requesterRef, {
        friends: [...new Set([...(requesterData.friends || []), user.uid])],
        outgoingRequests: (requesterData.outgoingRequests || []).filter((id) => id !== user.uid),
      }),
    ]);

    window.location.reload();
  };

  // Determine the scanned profile's buddy-relationship status against the
  // CURRENT user's own doc (never trusted from the scanned profile itself).
  const resolveRelation = (scannedId, currentData) => {
    if (scannedId === user.uid) return "self";
    if ((currentData.friends || []).includes(scannedId)) return "buddies";
    if ((currentData.outgoingRequests || []).includes(scannedId)) return "requestSent";
    if ((currentData.incomingRequests || []).includes(scannedId)) return "requestReceived";
    return "none";
  };

  // Identify-only: decode → look up the profile → show the result bubble.
  // No Firestore write happens here — adding a buddy is always a separate,
  // explicit action (see sendBuddyRequest/acceptFromBubble below).
  const handleScan = async (data) => {
    qrDebug("raw decoded text:", data);
    if (!data) return;

    // Stop scanning the instant a QR is decoded — never keep the camera
    // looping after a successful read.
    setShowScanner(false);
    setScanError("");
    setScannedProfile(null);
    setScannedAction("idle");

    const scannedId = extractUidFromScanPayload(data);
    qrDebug("extracted uid:", scannedId);

    if (!scannedId) {
      qrDebug("rejected: payload is not a recognizable PartyBuddy uid");
      setScanError(`❗ ${t("buddies.invalidQr", "This QR code isn't a PartyBuddy buddy code.")}`);
      return;
    }

    try {
      const [buddySnap, currentSnap] = await Promise.all([
        getDoc(doc(db, "users", scannedId)),
        getDoc(doc(db, "users", user.uid)),
      ]);

      if (!buddySnap.exists()) {
        qrDebug("rejected: no user document for uid", scannedId);
        setScanError(`❗ ${t("buddies.notFound", "No user found for this QR code.")}`);
        return;
      }

      const currentData = currentSnap.data() || {};
      const relation = resolveRelation(scannedId, currentData);
      setScannedProfile({ id: scannedId, ...buddySnap.data(), relation });
    } catch (err) {
      qrDebug("error while resolving scanned uid:", err);
      setScanError(t("buddies.scanError", "Error processing the QR code."));
    }
  };

  // Explicit "Add Buddy" tap from the result bubble — sends the buddy
  // request only now, never automatically after a scan.
  const sendBuddyRequest = async (scannedId) => {
    setScannedAction("sending");
    try {
      const currentRef = doc(db, "users", user.uid);
      const targetRef = doc(db, "users", scannedId);
      const [currentSnap, targetSnap] = await Promise.all([getDoc(currentRef), getDoc(targetRef)]);
      const currentData = currentSnap.data() || {};
      const targetData = targetSnap.data() || {};

      await Promise.all([
        updateDoc(currentRef, {
          outgoingRequests: [...new Set([...(currentData.outgoingRequests || []), scannedId])],
        }),
        updateDoc(targetRef, {
          incomingRequests: [...new Set([...(targetData.incomingRequests || []), user.uid])],
        }),
      ]);

      setScannedAction("sent");
      setScannedProfile((prev) => (prev ? { ...prev, relation: "requestSent" } : prev));
    } catch (err) {
      qrDebug("error sending buddy request:", err);
      setScannedAction("idle");
      setScanError(t("buddies.scanError", "Error processing the QR code."));
    }
  };

  // Accept directly from the bubble when the scanned profile already sent
  // the current user a request (reuses the same logic as the pending list).
  const acceptFromBubble = async (scannedId) => {
    setScannedAction("sending");
    await acceptRequest(scannedId);
  };


  // Mount/tear down the actual camera scanner whenever the scanner panel is
  // opened/closed (and on unmount). We deliberately manage getUserMedia and
  // the <video> element ourselves instead of delegating to a third-party
  // scanner library: several such libraries (react-qr-reader, html5-qrcode)
  // can end up with an active MediaStream (camera light on, permission
  // granted) that is never actually painted to the visible <video> element
  // on some Android/Chromium-based mobile browsers — the stream exists, but
  // nothing guarantees `video.play()` runs on *that exact* element after
  // `srcObject` is assigned. Doing it by hand lets us guarantee: the video
  // element that is visibly rendered on screen is the same element that
  // gets `srcObject` set, has `autoplay`/`playsInline`/`muted` set directly
  // as DOM properties (required for inline, non-user-gesture playback on
  // mobile), and has `.play()` explicitly (and repeatedly, if needed)
  // invoked on it.
  useEffect(() => {
    if (!showScanner) return undefined;

    let cancelled = false;
    let stream = null;
    let rafId = null;
    let lastDecodeAt = 0;
    // Decoding every animation frame (~60/s) is unnecessary work and can
    // actually hurt reliability on mobile (thermal throttling, main-thread
    // contention with camera autofocus). Re-running jsQR every ~200ms is
    // plenty responsive while giving the camera time to focus between tries.
    const DECODE_INTERVAL_MS = 200;
    setScanError("");

    const stopCamera = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (stream) {
        // Stop every track immediately so the OS/browser camera indicator
        // turns off as soon as the scanner closes or unmounts.
        stream.getTracks().forEach((track) => track.stop());
        stream = null;
      }
      const video = videoRef.current;
      if (video) {
        video.pause();
        video.srcObject = null;
      }
    };

    // Reads frames from the live <video> element via an offscreen canvas
    // and hands the pixel data to jsQR — completely decoupled from however
    // the video got its stream, so it works with any getUserMedia video.
    // Always decodes the FULL native video frame (video.videoWidth/Height),
    // never a CSS-cropped region — the visible preview can stay `object-cover`
    // without affecting what jsQR actually sees, so a QR near the edge of the
    // cropped preview is still decodable.
    const tick = (timestamp) => {
      if (cancelled) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const dueForDecode = timestamp - lastDecodeAt >= DECODE_INTERVAL_MS;
      if (dueForDecode && video && canvas && video.readyState >= video.HAVE_CURRENT_DATA && video.videoWidth > 0) {
        lastDecodeAt = timestamp;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        // "attemptBoth" also tries the color-inverted image in the same
        // pass — important for QR codes photographed off another phone/
        // monitor screen, where photographing in dark mode, screen glare,
        // or moiré can effectively flip local contrast versus a printed QR.
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "attemptBoth",
        });
        qrDebug("decode attempt", { width: canvas.width, height: canvas.height, found: !!code });
        if (code && code.data) {
          handleScan(code.data);
          // handleScan() closes the scanner (setShowScanner(false)); the
          // effect's cleanup below will stop the camera on the next render.
          return;
        }
      }
      rafId = requestAnimationFrame(tick);
    };

    const startCamera = async () => {
      // Try the rear/environment camera first, then fall back to whatever
      // camera is available (e.g. laptops with only a front camera).
      const attempts = [{ video: { facingMode: { ideal: "environment" } }, audio: false }, { video: true, audio: false }];
      let lastError = null;
      for (const constraints of attempts) {
        try {
          // eslint-disable-next-line no-await-in-loop
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          break;
        } catch (err) {
          lastError = err;
        }
      }

      if (cancelled) {
        if (stream) stream.getTracks().forEach((track) => track.stop());
        return;
      }

      if (!stream) {
        const message = String(lastError?.name || lastError?.message || "");
        const isPermissionIssue = /permission|notallowed|denied/i.test(message);
        setScanError(
          isPermissionIssue
            ? `❗ ${t("buddies.cameraPermission", "Camera access failed. Please allow camera permission.")}`
            : `❗ ${t("buddies.noCamera", "No usable camera could be started on this device.")}`
        );
        return;
      }

      const video = videoRef.current;
      if (!video) {
        stream.getTracks().forEach((track) => track.stop());
        stream = null;
        return;
      }

      // Set these as real DOM properties (not just JSX props) — mobile
      // Chromium/WebKit require playsInline + muted to be present on the
      // element itself before/at the time playback starts for inline
      // (non-fullscreen), autoplay-without-gesture video to actually render
      // frames instead of staying black.
      video.muted = true;
      video.setAttribute("muted", "");
      video.setAttribute("autoplay", "");
      video.playsInline = true;
      video.setAttribute("playsinline", "");
      video.srcObject = stream;

      try {
        await video.play();
      } catch (err) {
        // Autoplay can be rejected in rare cases; a muted, inline video
        // triggered by the user's "Add buddy" tap should not hit this, but
        // retry once defensively rather than silently staying black.
        try {
          await video.play();
        } catch (retryErr) {
          if (!cancelled) {
            setScanError(`❗ ${t("buddies.noCamera", "No usable camera could be started on this device.")}`);
          }
          return;
        }
      }

      if (cancelled) return;
      rafId = requestAnimationFrame(tick);
    };

    startCamera();

    return () => {
      cancelled = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showScanner]);

  return (
    <div className="flex h-[calc(100dvh-var(--navbar-h,64px))] w-full max-w-full overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-900 p-4 md:p-6 flex-col lg:flex-row">
      {/* Buddy-Liste — on mobile, hidden while a chat is open (mobile is a
          single-panel list→chat flow); always shown side-by-side on lg+. */}
      <div
        className={`${selectedBuddy ? "hidden" : "flex"} lg:flex w-full lg:w-1/3 min-h-0 flex-col overflow-y-auto bg-slate-800/90 backdrop-blur-sm p-4 md:p-6 rounded-2xl shadow-xl mb-6 lg:mb-0 lg:mr-6 border border-slate-700/50`}
      >
        <h1 className="text-3xl font-bold mb-6 text-center text-white">{t("buddies.title", "My Buddies")}</h1>

        <div className="text-center mb-6">
          <button
            onClick={() => setShowScanner(!showScanner)}
            className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-500 hover:to-emerald-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            {showScanner ? t("buddies.closeScanner", "Close scanner") : t("buddies.addBuddy", "Add buddy")}
          </button>
        </div>

        {incomingRequests.length > 0 && (
          <div className="mb-6 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100">
            <p className="font-semibold mb-2">{t('buddies.pending', 'Pending')}</p>
            {incomingRequests.map((id) => (
              <div key={id} className="flex items-center justify-between gap-3 rounded-xl bg-slate-800/80 p-3 mt-2">
                <span>{id}</span>
                <button onClick={() => acceptRequest(id)} className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white">{t("buddies.accept", "Accept")}</button>
              </div>
            ))}
          </div>
        )}

        {showScanner && (
          <div className="mb-6">
            <div className="relative mx-auto aspect-square w-full max-w-[280px] overflow-hidden rounded-2xl border-2 border-emerald-500/60 bg-black shadow-lg sm:max-w-xs">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="h-full w-full object-cover"
              />
              {/* Offscreen frame-grab canvas used only for QR decoding — never shown. */}
              <canvas ref={canvasRef} className="hidden" />
            </div>
            <p className="mt-2 text-center text-xs text-slate-400">
              {t("buddies.scanHint", "Point the camera at your buddy's QR code. Allow camera access if prompted.")}
            </p>
          </div>
        )}

        {/* Error feedback is shown outside the scanner panel too, since a
            successful decode closes the scanner immediately (never keep
            scanning after a successful read) — the message must still be
            visible after that happens. A successful scan instead opens the
            profile bubble below rather than a plain status line. */}
        {scanError && (
          <p className="text-sm text-red-400 text-center mb-4 font-medium">{scanError}</p>
        )}

        {buddiesError && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
            <span>⚠️ {buddiesError}</span>
            <button
              type="button"
              onClick={() => setReloadToken((n) => n + 1)}
              className="rounded-lg border border-red-400/60 bg-red-500/20 px-3 py-1 text-xs font-semibold text-red-100 transition hover:bg-red-500/30"
            >
              {t("common.retry", "Retry")}
            </button>
          </div>
        )}

        {buddies.length === 0 ? (
          <p className="text-center text-gray-400">{t("buddies.noBuddies", "No buddies found yet.")}</p>
        ) : (
          <div className="space-y-3 overflow-y-auto">
            {sortedBuddies.map((buddy) => {
              const unread = chatNotifications?.unreadByBuddy?.[buddy.id] || 0;
              const entry = chatsByBuddy[buddy.id];
              return (
                <div
                  key={buddy.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openChat(buddy)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") openChat(buddy);
                  }}
                  className={`w-full cursor-pointer text-left bg-slate-700 p-3 sm:p-4 rounded-xl shadow-lg relative hover:bg-slate-600 transition-all duration-200 border border-slate-600 ${
                    selectedBuddy?.id === buddy.id ? "border-2 border-blue-400 shadow-blue-400/50" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Avatar src={buddy.photoUrl} avatarConfig={buddy.avatarConfig} size={44} alt={buddy.username} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="truncate font-bold text-white flex items-center gap-2">
                          {buddy.username}
                        </h3>
                        {entry?.lastMessageAtMillis > 0 && (
                          <span className="shrink-0 text-[11px] text-gray-400">
                            {formatChatTimestamp(entry.lastMessageAtMillis, language)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm text-gray-300">{getPreviewText(buddy)}</p>
                        {unread > 0 && (
                          <span className="inline-flex min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                            {unread > 9 ? "9+" : unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-3">
                    <a
                      href={`/profile?uid=${buddy.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm bg-gradient-to-r from-gray-600 to-slate-600 text-white px-3 py-1.5 rounded-lg font-medium hover:from-gray-500 hover:to-slate-500 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      {t("buddies.profileBtn", "Profile")}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Chatbereich — on mobile, only shown once a buddy is selected
          (single-panel flow with an explicit back button); on lg+ the
          desktop split-view (list + chat always both visible) is unchanged. */}
      <div
        className={`${selectedBuddy ? "flex" : "hidden"} lg:flex flex-1 min-w-0 min-h-0 flex-col bg-slate-800/90 backdrop-blur-sm p-4 md:p-6 rounded-2xl shadow-xl border border-slate-700/50`}
      >
        {selectedBuddy ? (
          <>
            <div className="flex items-center gap-3 mb-4 sm:mb-6 shrink-0">
              <button
                type="button"
                onClick={closeChat}
                className="lg:hidden shrink-0 text-white hover:text-blue-400 transition font-bold"
              >
                ← {t("buddies.backToList", "Back to list")}
              </button>
              <h2 className="flex min-w-0 flex-1 items-center justify-center gap-3 text-xl sm:text-2xl font-bold text-white">
                <Avatar src={selectedBuddy.photoUrl} avatarConfig={selectedBuddy.avatarConfig} size={36} alt={selectedBuddy.username} />
                <span className="truncate">💬 {selectedBuddy.username}</span>
              </h2>
            </div>

            {chatError && (
              <div className="mb-4 shrink-0 rounded-lg border border-red-700 bg-red-900/50 p-3 text-sm font-medium text-red-200">
                {chatError === "sendError"
                  ? t("chat.sendError", "Failed to send message.")
                  : t("buddies.conversationLoadError", "Could not load the conversation.")}
              </div>
            )}

            {chatLoading ? (
              <div className="flex flex-1 items-center justify-center text-gray-400">
                {t("buddies.loadingConversation", "Loading conversation…")}
              </div>
            ) : (
              <div className="relative flex-1 min-h-0 mb-4">
                <ChatMessageList
                  messages={messages}
                  currentUid={user.uid}
                  containerRef={containerRef}
                  bottomRef={bottomRef}
                  emptyLabel={t("chat.empty", "No messages yet. Start the conversation! 👋")}
                  className="h-full overflow-y-auto rounded-lg border border-slate-600 bg-slate-700 p-4"
                />
                {hasNewBelow && (
                  <button
                    type="button"
                    onClick={() => scrollToBottom("smooth")}
                    className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white shadow-lg transition hover:bg-blue-500"
                  >
                    ↓ {t("chat.newMessagesIndicator", "New messages")}
                  </button>
                )}
              </div>
            )}

            <div className="shrink-0" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
              <MessageComposer onSend={sendMessage} sending={sending} disabled={chatLoading} />
            </div>
          </>
        ) : (
          <p className="text-center text-gray-400 mt-20 text-lg">
            {t("buddies.selectBuddy", "Select a buddy to chat.")}
          </p>
        )}
      </div>

      {/* QR scan result bubble — identifies the scanned profile first;
          adding/accepting a buddy always requires an explicit tap here. */}
      {scannedProfile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4"
          onClick={() => setScannedProfile(null)}
        >
          <div
            className="w-full max-w-xs rounded-3xl border border-slate-700/70 bg-slate-800/98 p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setScannedProfile(null)}
              className="float-right -mt-2 -mr-2 text-xl text-slate-400 transition hover:text-white"
              aria-label={t("drawer.close", "Close")}
            >
              ✕
            </button>
            <Avatar src={scannedProfile.photoUrl} avatarConfig={scannedProfile.avatarConfig} size={72} alt={scannedProfile.username} className="mx-auto" />
            <h3 className="mt-3 text-lg font-bold text-white">{scannedProfile.username || t("buddies.unknownUser", "PartyBuddy user")}</h3>
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
              {getRoleLabel(scannedProfile.role, language)}
            </p>
            {scannedProfile.city && (
              <p className="mt-1 text-sm text-slate-300">📍 {scannedProfile.city}</p>
            )}

            <div className="mt-5">
              {scannedProfile.relation === "self" && (
                <span className="inline-block rounded-xl bg-slate-700 px-4 py-2 text-sm font-semibold text-slate-200">
                  {t("buddies.thisIsYou", "This is you")}
                </span>
              )}
              {scannedProfile.relation === "buddies" && (
                <span className="inline-block rounded-xl bg-slate-700 px-4 py-2 text-sm font-semibold text-slate-200">
                  {t("buddies.alreadyBuddies", "Already buddies")}
                </span>
              )}
              {scannedProfile.relation === "requestSent" && (
                <span className="inline-block rounded-xl bg-slate-700 px-4 py-2 text-sm font-semibold text-slate-200">
                  {t("buddies.requestSentLabel", "Request sent")}
                </span>
              )}
              {scannedProfile.relation === "requestReceived" && (
                <button
                  type="button"
                  onClick={() => acceptFromBubble(scannedProfile.id)}
                  disabled={scannedAction === "sending"}
                  className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:from-emerald-500 hover:to-green-500 disabled:opacity-60"
                >
                  {scannedAction === "sending" ? t("common.loadingMore", "Loading…") : t("buddies.accept", "Accept")}
                </button>
              )}
              {scannedProfile.relation === "none" && (
                <button
                  type="button"
                  onClick={() => sendBuddyRequest(scannedProfile.id)}
                  disabled={scannedAction === "sending" || scannedAction === "sent"}
                  className="w-full rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:from-green-500 hover:to-emerald-500 disabled:opacity-60"
                >
                  {scannedAction === "sending"
                    ? t("common.loadingMore", "Loading…")
                    : scannedAction === "sent"
                    ? t("buddies.requestSentLabel", "Request sent")
                    : t("buddies.addBuddy", "Add buddy")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Buddies;